/**
 * Iter60.p49.1 — Searchable project picker with Job # prefix.
 *
 * Shadcn `Select` doesn't support type-to-search, and the user needs
 * both a `{job_number} · {name}` display label AND substring search
 * over BOTH fields. Rather than reinvent EmployeePicker, this is a
 * tiny purpose-built Popover + Command dropdown that:
 *
 *   * Renders `{code} · {name}` when `code` (Job #) is present, and
 *     falls back to just `{name}` when it isn't.
 *   * Searches over `code + " " + name` (case-insensitive) via
 *     CommandInput.
 *   * Preserves the shape of the shadcn Select props the caller uses:
 *     `value` (project_id string) + `onValueChange` (id) — so drop-in
 *     replacement in the Split dialog.
 *
 * Used by `SplitByProjectsDialog.jsx` and the Attendance chip tooltip.
 * Exported `formatProjectLabel(project)` is the shared formatter every
 * project-referencing surface should use so labels stay identical.
 */
import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";

export function formatProjectLabel(project) {
    if (!project) return "";
    const code = (project.code || "").trim();
    const name = (project.name || "").trim();
    if (code && name) return `${code} · ${name}`;
    return name || code || "";
}

export default function ProjectPicker({
    projects, value, onValueChange, placeholder = "Select project",
    testid = "project-picker", disabled = false,
    // Iter67 F1 — When true, render a fixed "— No project —" option at
    // the top of the list (not affected by the search filter). Selecting
    // it fires `onValueChange("")` so callers can treat empty string as
    // "no project selected".
    allowNone = false,
    noneLabel = "— No project —",
    // Iter91 — When true, render a dedicated "— Unassigned —" option
    // right below the None option. Selecting it fires
    // `onValueChange("__unassigned__")` so callers can translate it to
    // the backend's `project_id="none"` sentinel for "rows without a
    // project assigned". Kept separate from `allowNone` because the
    // Attendance filter bar wants THREE distinct options in a single
    // dropdown: All projects / Unassigned / specific project.
    allowUnassigned = false,
    unassignedLabel = "— Unassigned —",
}) {
    const [open, setOpen] = useState(false);
    const selected = (projects || []).find((p) => p.id === value);
    const isUnassignedSelected = value === "__unassigned__";
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    disabled={disabled}
                    aria-expanded={open}
                    className="h-8 w-full justify-between font-normal text-xs"
                    data-testid={testid}
                >
                    <span className="truncate">
                        {selected ? formatProjectLabel(selected) : (
                            <span className="text-[color:var(--asl-muted)]">
                                {isUnassignedSelected
                                    ? unassignedLabel
                                    : allowNone && (value === "" || value === null || value === undefined)
                                        ? noneLabel
                                        : placeholder}
                            </span>
                        )}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[320px]" align="start">
                <Command
                    filter={(itemValue, search) => {
                        // Iter67 F1 — the "no project" sentinel always
                        // ranks 1 regardless of the search string so
                        // it stays clickable as the operator narrows
                        // the list. Iter91 — same guarantee for the
                        // dedicated "unassigned" sentinel.
                        if (itemValue === "__none_sentinel__") return 1;
                        if (itemValue === "__unassigned_sentinel__") return 1;
                        return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                    }}
                >
                    <CommandInput
                        placeholder="Search Job # or name…"
                        data-testid={`${testid}-search`}
                    />
                    <CommandList>
                        <CommandEmpty data-testid={`${testid}-empty`}>
                            No projects match.
                        </CommandEmpty>
                        <CommandGroup>
                            {allowNone && (
                                <CommandItem
                                    key="__none__"
                                    value="__none_sentinel__"
                                    onSelect={() => {
                                        onValueChange?.("");
                                        setOpen(false);
                                    }}
                                    data-testid={`${testid}-item-none`}
                                >
                                    <Check
                                        className={`mr-2 h-3.5 w-3.5 ${(!value && !isUnassignedSelected) ? "opacity-100" : "opacity-0"}`}
                                    />
                                    <span className="truncate text-xs italic text-[color:var(--asl-muted)]">
                                        {noneLabel}
                                    </span>
                                </CommandItem>
                            )}
                            {allowUnassigned && (
                                <CommandItem
                                    key="__unassigned__"
                                    value="__unassigned_sentinel__"
                                    onSelect={() => {
                                        onValueChange?.("__unassigned__");
                                        setOpen(false);
                                    }}
                                    data-testid={`${testid}-item-unassigned`}
                                >
                                    <Check
                                        className={`mr-2 h-3.5 w-3.5 ${isUnassignedSelected ? "opacity-100" : "opacity-0"}`}
                                    />
                                    <span className="truncate text-xs italic text-[color:var(--asl-muted)]">
                                        {unassignedLabel}
                                    </span>
                                </CommandItem>
                            )}
                            {(projects || []).map((p) => {
                                const label = formatProjectLabel(p);
                                // Command matches against `value` — pack
                                // both code + name into it so a search
                                // for either substring hits.
                                const searchValue = `${p.code || ""} ${p.name || ""} ${p.id}`;
                                return (
                                    <CommandItem
                                        key={p.id}
                                        value={searchValue}
                                        onSelect={() => {
                                            onValueChange?.(p.id);
                                            setOpen(false);
                                        }}
                                        data-testid={`${testid}-item-${p.id}`}
                                    >
                                        <Check
                                            className={`mr-2 h-3.5 w-3.5 ${p.id === value ? "opacity-100" : "opacity-0"}`}
                                        />
                                        <span className="truncate text-xs" title={label}>
                                            {label}
                                        </span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
